import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import {
  useBulletinVariableQuery,
  useBulletinReportQuery,
  useNavLinksQuery,
} from '../../services/api';

import Card from '../Card/Card';
// @ts-expect-error Will convert later
import Loader from '../Loader/Loader';

import './BulletinView.scss';
import MenuDisplay from './MenuDisplay';
// @ts-expect-error Will convert later
import ReportView from '../ReportView/ReportView';
import VariableView from '../VariableView/VariableView';
// @ts-expect-error Will convert later
import ViewTitle from '../ViewTitle/ViewTitle';
import BulletinDatePicker from './BulletinDatePicker';

import {
  getOrderedEntries,
  getPrevNextEntry,
  getEntryLink,
} from './Bulletin.helper';
import MessageBulletinDate from './MessageBulletinDate';
import ReviewProgression from './ReviewProgression';
import { useBulletin } from '../../context/bulletin';
import ValidationButton from './Modal/ValidationButton';

export interface BulletinViewProps {
  setMapsAsSeen: () => void;
  userIsAuthenticated: boolean;
  userHasBulletinPermission: boolean;
}

const BulletinView = ({
  setMapsAsSeen,
  userIsAuthenticated,
  userHasBulletinPermission,
}: BulletinViewProps) => {
  const {
    bulletinDate: date,
    reportId: reportIdToDisplay = null,
    variableId: variableTreeKeyToDisplay = null,
  } = useParams();
  const [disableFlag, setDisableFlag] = useState(true);
  const [entriesStatus, setEntriesStatus] = useState<
    | Record<
        string,
        {
          isFlagged: boolean;
          isUnseen: boolean;
          treeKey: string;
        }
      >
    | undefined
  >(undefined);

  const {
    bulletinType,
    bulletinDate,
    isLatestBulletin,
    bulletinResponse: bulletin,
    isLoading,
    error,
  } = useBulletin();

  const { data: dataNavLinks, isLoading: isLoadingNavLinks } =
    useNavLinksQuery();

  const generateEntriesStatus = useCallback(() => {
    const nextEntriesStatus: Record<
      string,
      {
        isFlagged: boolean;
        isUnseen: boolean;
        treeKey: string;
      }
    > = {};
    bulletin?.systems.forEach(system => {
      system.categories.forEach(category => {
        category.entries.forEach(({ flag, unseen, tree_key: treeKey }) => {
          nextEntriesStatus[treeKey] = {
            isFlagged: flag,
            isUnseen: unseen,
            treeKey,
          };
        });
      });
    });
    return nextEntriesStatus;
  }, [bulletin]);

  const currentEntryKey = useMemo(
    () =>
      entriesStatus?.[variableTreeKeyToDisplay ?? Object.keys(entriesStatus)[0]]
        ?.treeKey,
    [entriesStatus, variableTreeKeyToDisplay],
  );

  const { data: variableToDisplay, isLoading: isLoadingVariable } =
    useBulletinVariableQuery(bulletinType, currentEntryKey, bulletinDate);

  const { data: reportToDisplay, isLoading: isLoadingReport } =
    useBulletinReportQuery(reportIdToDisplay);

  useEffect(() => {
    if (bulletin?.systems) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setEntriesStatus(generateEntriesStatus());
    }
    setDisableFlag(!!bulletin?.bulletin.isValidated);
  }, [bulletin, generateEntriesStatus]);

  const updateEntryStatus = useCallback(
    (entry, status) => {
      if (!isLatestBulletin) {
        return;
      }
      setEntriesStatus(prevEntriesStatus => {
        const item = entry.tree_key;
        if (prevEntriesStatus) {
          return {
            ...prevEntriesStatus,
            [item]: {
              ...prevEntriesStatus[item],
              ...status,
            },
          };
        }
        return {
          [item]: {
            ...status,
          },
        };
      });
    },
    [isLatestBulletin],
  );

  if (!bulletinType) {
    if (isLoadingNavLinks) {
      return <Loader>Loading bulletin...</Loader>;
    }
    const firstBulletinName = dataNavLinks?.bulletinLinks[0]?.code;
    return <Navigate to={`/bulletin/${firstBulletinName}`} replace />;
  }

  if (isLoading) {
    return <Loader>Loading bulletin...</Loader>;
  }

  if (!date) {
    return (
      <Navigate to={`/bulletin/${bulletinType}/${bulletinDate}`} replace />
    );
  }

  const orderedEntries = bulletin?.systems
    ? getOrderedEntries(bulletin.systems)
    : [];

  if (
    !variableTreeKeyToDisplay &&
    !reportIdToDisplay &&
    orderedEntries.length
  ) {
    const [{ tree_key: treeKey, type }] = orderedEntries;

    return (
      <Navigate
        to={`/bulletin/${bulletinType}/${bulletinDate}/${type[0]}/${treeKey}`}
        replace
      />
    );
  }

  const displayVariable =
    entriesStatus !== undefined &&
    variableTreeKeyToDisplay !== null &&
    variableToDisplay?.graphs;
  const displayReport = reportIdToDisplay && reportToDisplay;

  const entryTreeKey = variableTreeKeyToDisplay ?? reportIdToDisplay;
  const entryType = variableTreeKeyToDisplay ? 'variable' : 'report';

  const prevEntry = getPrevNextEntry(
    orderedEntries,
    entryTreeKey,
    entryType,
    'prev',
  );
  const nextEntry = getPrevNextEntry(
    orderedEntries,
    entryTreeKey,
    entryType,
    'next',
  );
  const prevEntryLink = getEntryLink(bulletinType, bulletinDate, prevEntry);
  const nextEntryLink = getEntryLink(bulletinType, bulletinDate, nextEntry);

  const isBulletinNotFound =
    !!error && 'status' in error && error.status === 404;

  return (
    <>
      <ViewTitle pageTitle="Bulletin" />
      <div className="BulletinView">
        <Card className="nav">
          <div className="bulletin-info">
            {bulletin?.bulletin.validation_enabled &&
              userIsAuthenticated &&
              userHasBulletinPermission && (
                <ReviewProgression userIsAuthenticated={userIsAuthenticated} />
              )}
            {bulletin?.bulletin.last_update && (
              <div>Last update: {bulletin.bulletin.last_update}</div>
            )}
            <BulletinDatePicker />
            <ValidationButton
              userIsAuthenticated={userIsAuthenticated && isLatestBulletin}
              userHasBulletinPermission={userHasBulletinPermission}
            />
          </div>
          {bulletin && (
            <div className="categories">
              <MenuDisplay
                userIsAuthenticated={
                  userIsAuthenticated &&
                  userHasBulletinPermission &&
                  bulletin.bulletin.validation_enabled &&
                  !!isLatestBulletin
                }
                entryTreeKey={entryTreeKey}
                entryType={entryType}
              />
            </div>
          )}
        </Card>
        <div className="main">
          {isBulletinNotFound ? (
            <MessageBulletinDate
              message="There is no bulletin on"
              isDisplayedOnDayDate
            />
          ) : (
            <MessageBulletinDate />
          )}
          {(isLoadingVariable || isLoadingReport) && (
            <Loader>Loading {entryType}...</Loader>
          )}
          {!isBulletinNotFound && (displayVariable || displayReport) && (
            <>
              {displayVariable ? (
                <VariableView
                  variableToDisplay={variableToDisplay}
                  variableTreeKeyToDisplay={variableTreeKeyToDisplay}
                  disableFlag={disableFlag}
                  setMapsAsSeen={setMapsAsSeen}
                  updateEntryStatus={updateEntryStatus}
                  userIsAuthenticated={
                    userIsAuthenticated &&
                    userHasBulletinPermission &&
                    isLatestBulletin
                  }
                  prevEntryLink={prevEntryLink}
                  nextEntryLink={nextEntryLink}
                />
              ) : null}

              {displayReport ? (
                <ReportView
                  reportToDisplay={reportToDisplay}
                  updateEntryStatus={updateEntryStatus}
                  disableFlag={disableFlag}
                  userIsAuthenticated={
                    userIsAuthenticated &&
                    userHasBulletinPermission &&
                    isLatestBulletin
                  }
                  bulletinDate={bulletin?.bulletin.date}
                  prevEntryLink={prevEntryLink}
                  nextEntryLink={nextEntryLink}
                  validationEnabled={bulletin?.bulletin.validation_enabled}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default BulletinView;
